import { useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { keyedPath } from '@/shared/api/keyedPath';
import { useAuthStore } from '@/shared/auth/store';
import { usePrefState } from '@/shared/prefs/usePrefState';
import { readPref, writePref } from '@/shared/prefs/storage';

/**
 * slice-4478 (John 29 Oct, Q179 / #7257) — the two grid tick boxes next to "Clear filters":
 * <b>Header Filters</b> (AG-Grid's filter row under the column headings) and <b>Col Chooser</b>
 * (the Columns / Filters panel on the grid's right edge). PowerHouse has neither (ADR-0079
 * amendment); both start OFF and each user turns them on for themselves.
 *
 * <p><b>Where it lives.</b> One setting per user covering every grid, server-side in the user's
 * PH10 settings (dbxx.ph10_usr_setting via /api/users/settings, category Window, key GridChrome;
 * the same store as the Auto-hide default, slice-4474), so it follows the user to any PC and
 * survives logout. Its value is JSON {@code {"headerFilters":bool,"colChooser":bool}}. Every
 * browser keeps a local mirror (usePrefState) that the grids read, so a grid never waits on a
 * request; {@link useGridChromeSync}, mounted once in the app header, copies the server value
 * into the mirror at sign-in.
 */
export const GRID_CHROME_SETTING_CATEGORY = 'Window';
export const GRID_CHROME_SETTING_KEY = 'GridChrome';
/** Local mirrors (JSON booleans under the user's pref namespace). */
export const HEADER_FILTERS_PREF = 'grid.headerFilters';
export const COL_CHOOSER_PREF = 'grid.colChooser';

export interface GridChrome {
  headerFilters: boolean;
  colChooser: boolean;
}

export const GRID_CHROME_DEFAULT: GridChrome = { headerFilters: false, colChooser: false };

export function parseGridChrome(raw: string | null | undefined): GridChrome | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<GridChrome>;
    if (typeof v.headerFilters === 'boolean' && typeof v.colChooser === 'boolean') {
      return { headerFilters: v.headerFilters, colChooser: v.colChooser };
    }
  } catch {
    /* not ours — ignore */
  }
  return null;
}

/** Copy a server value into this browser's mirror (only the keys that differ are written). */
export function applyGridChrome(userId: string, g: GridChrome): void {
  if (!userId) return;
  if (readPref<boolean>(userId, HEADER_FILTERS_PREF, GRID_CHROME_DEFAULT.headerFilters) !== g.headerFilters) {
    writePref(userId, HEADER_FILTERS_PREF, g.headerFilters);
  }
  if (readPref<boolean>(userId, COL_CHOOSER_PREF, GRID_CHROME_DEFAULT.colChooser) !== g.colChooser) {
    writePref(userId, COL_CHOOSER_PREF, g.colChooser);
  }
}

const SETTING_IDS = { category: GRID_CHROME_SETTING_CATEGORY, key: GRID_CHROME_SETTING_KEY };
const settingPath = () => keyedPath('/api/users/settings/{category}/{key}', SETTING_IDS);
const effectivePath = () => keyedPath('/api/users/settings/{category}/{key}/effective', SETTING_IDS);

/**
 * The SYNC: mounted once in the app header (ProfileMenu); reads the server value at sign-in and
 * applies it to this browser's mirror. A user with no saved value keeps the default (both off).
 */
export function useGridChromeSync(): void {
  const userId = useAuthStore((s) => s.session?.userId ?? '');
  const q = useQuery<GridChrome | null>({
    queryKey: ['settings', 'effective', GRID_CHROME_SETTING_CATEGORY, GRID_CHROME_SETTING_KEY, userId],
    queryFn: async () => {
      try {
        const res = await api<{ value: string | null }>(effectivePath());
        return parseGridChrome(res?.value ?? null);
      } catch (e) {
        if (e instanceof Error && 'status' in e && (e as { status?: number }).status === 404) return null;
        throw e;
      }
    },
    enabled: userId !== '',
    staleTime: 60_000,
    retry: false,
  });
  useEffect(() => {
    if (q.data) applyGridChrome(userId, q.data);
  }, [q.data, userId]);
}

/**
 * What a grid reads: the two flags plus setters. A setter flips the mirror at once (every grid in
 * every tab of this browser follows) and saves both flags for the user's other PCs; a failed save
 * leaves this browser's choice in place.
 */
export function useGridChrome(): GridChrome & {
  setHeaderFilters: (on: boolean) => void;
  setColChooser: (on: boolean) => void;
} {
  const userId = useAuthStore((s) => s.session?.userId ?? '');
  const [headerFilters, setHeaderFiltersPref] = usePrefState<boolean>(HEADER_FILTERS_PREF, GRID_CHROME_DEFAULT.headerFilters);
  const [colChooser, setColChooserPref] = usePrefState<boolean>(COL_CHOOSER_PREF, GRID_CHROME_DEFAULT.colChooser);

  const save = useCallback(
    (g: GridChrome) => {
      if (!userId) return;
      void api<unknown>(settingPath(), { method: 'PUT', json: { value: JSON.stringify(g) } }).catch(() => {
        /* kept locally; the next change retries */
      });
    },
    [userId],
  );

  const setHeaderFilters = useCallback(
    (on: boolean) => {
      setHeaderFiltersPref(on);
      save({ headerFilters: on, colChooser: readPref<boolean>(userId, COL_CHOOSER_PREF, GRID_CHROME_DEFAULT.colChooser) });
    },
    [setHeaderFiltersPref, save, userId],
  );
  const setColChooser = useCallback(
    (on: boolean) => {
      setColChooserPref(on);
      save({ headerFilters: readPref<boolean>(userId, HEADER_FILTERS_PREF, GRID_CHROME_DEFAULT.headerFilters), colChooser: on });
    },
    [setColChooserPref, save, userId],
  );

  return { headerFilters, colChooser, setHeaderFilters, setColChooser };
}
